export type GeofenceType = 'circle' | 'rectangle' | 'custom';

export interface WorkshopGeofence {
  type: GeofenceType;
  center: { lat: number; lng: number };
  radiusMeters?: number;
  coordinates?: { lat: number; lng: number }[];
}

export interface WorkshopGeometry {
  wkt: string;
  geometryType: GeofenceType;
  radiusMeters: number | null;
}

export function geofenceToWkt(geofence: WorkshopGeofence): WorkshopGeometry {
  switch (geofence.type) {
    case 'circle':
      return {
        wkt: `POINT(${geofence.center.lng} ${geofence.center.lat})`,
        geometryType: 'circle',
        radiusMeters: geofence.radiusMeters ?? null
      };
    case 'rectangle':
    case 'custom':
      if (!geofence.coordinates?.length) {
        throw new Error('No coordinates for polygon geofence');
      }
      const coords = [...geofence.coordinates, geofence.coordinates[0]];
      const ring = coords.map(c => `${c.lng} ${c.lat}`).join(', ');
      return {
        wkt: `POLYGON((${ring}))`,
        geometryType: geofence.type,
        radiusMeters: null
      };
  }
}

export function wktToGeofence(wkt: string, geometryType: GeofenceType, radiusMeters?: number | null): WorkshopGeofence {
  if (geometryType === 'circle') {
    const match = wkt.match(/POINT\s*\(\s*([^\s]+)\s+([^\s)]+)\s*\)/i);
    if (!match) {
      throw new Error('Invalid POINT WKT: ' + wkt);
    }
    const lng = parseFloat(match[1]);
    const lat = parseFloat(match[2]);
    return {
      type: 'circle',
      center: { lat, lng },
      radiusMeters: radiusMeters ?? 200
    };
  }

  // Rectangle or custom polygon
  const match = wkt.match(/POLYGON\s*\(\((.+)\)\)/i);
  if (!match) {
    throw new Error('Invalid POLYGON WKT: ' + wkt);
  }

  const coordinates = match[1]
    .split(',')
    .map(pair => {
      const [lng, lat] = pair.trim().split(/\s+/).map(Number);
      return { lat, lng };
    })
    .slice(0, -1); // Remove closing duplicate point

  const centroid = coordinates.reduce(
    (acc, c) => ({
      lat: acc.lat + c.lat / coordinates.length,
      lng: acc.lng + c.lng / coordinates.length
    }),
    { lat: 0, lng: 0 }
  );

  return {
    type: geometryType,
    center: centroid,
    coordinates
  };
}